import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import {
  Flame,
  PlayCircle,
  PauseCircle,
  RefreshCw,
  Activity,
  AlertTriangle,
  CheckCircle2,
  PowerOff,
  Bell,
  BellOff,
  Send,
  XCircle,
  MessageSquare,
  Mail,
} from "lucide-react";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { listWarmupAccounts, updateWarmup, runWarmupTickForUser } from "@/lib/warmup.functions";

export const Route = createFileRoute("/_authenticated/warmup")({
  component: WarmupPage,
  head: () => ({ meta: [{ title: "SMTP-Warmup — InboxPulse" }] }),
});

function WarmupPage() {
  const qc = useQueryClient();
  const listFn = useServerFn(listWarmupAccounts);
  const updateFn = useServerFn(updateWarmup);
  const tickFn = useServerFn(runWarmupTickForUser);

  const { data, isLoading } = useQuery({
    queryKey: ["warmup-accounts"],
    queryFn: () => listFn(),
  });

  type UpdateInput = {
    id: string;
    warmup_enabled?: boolean;
    warmup_start_limit?: number;
    warmup_current_limit?: number;
    warmup_max_limit?: number;
    warmup_growth_percent?: number;
    warmup_paused?: boolean;
    warmup_pause_reason?: string | null;
  };

  const mUpdate = useMutation({
    mutationFn: (vars: UpdateInput) => updateFn({ data: vars }),
    onSuccess: () => {
      toast.success("Warmup gespeichert.");
      qc.invalidateQueries({ queryKey: ["warmup-accounts"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const mTick = useMutation({
    mutationFn: () => tickFn(),
    onSuccess: (r) => {
      toast.success(`Tick: ${r.grown} gewachsen, ${r.paused} pausiert, ${r.skipped} übersprungen.`);
      qc.invalidateQueries({ queryKey: ["warmup-accounts"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="p-8">
      <PageHeader
        title="SMTP-Warmup"
        description="Pro SMTP-Konto Tageslimit automatisch steigern (Standard: +20%/Tag) und bei zu hoher Bounce-Rate stoppen."
        actions={
          <Button onClick={() => mTick.mutate()} disabled={mTick.isPending}>
            <RefreshCw className={`size-4 mr-2 ${mTick.isPending ? "animate-spin" : ""}`} />
            Jetzt prüfen
          </Button>
        }
      />

      <HelpPanel
        title="Hilfe: Neue Absender langsam aufwärmen"
        what={
          <>
            Warmup heißt: ein <b>neuer Absender</b> verschickt am Anfang nur wenige Mails am Tag und
            steigert sich automatisch. So glaubt Gmail/Outlook, dass du ein echter Mensch bist — und
            nicht ein Spammer.
          </>
        }
        why={
          <>
            Wenn du sofort 1000 Mails von einer brandneuen Adresse schickst, landest du garantiert
            im Spam und beschädigst deine Domain dauerhaft. Warmup verhindert das.
          </>
        }
        how={[
          <>
            Erst unter <b>SMTP-Versand</b> ein Konto anlegen.
          </>,
          <>
            Auf dieser Seite das Konto suchen — Warmup ist standardmäßig <b>an</b> (+20% pro Tag).
          </>,
          <>Start-Limit z.B. 20 Mails/Tag — nach einer Woche sind es schon ~70.</>,
          <>
            System pausiert <b>automatisch</b>, wenn die Bounce-Rate zu hoch wird.
          </>,
          <>
            <b>Jetzt prüfen</b> oben rechts simuliert einen Tageswechsel zum Testen.
          </>,
        ]}
        tips={[
          <>Mindestens 14 Tage Warmup, bevor du eine neue Adresse für echte Kampagnen nutzt.</>,
          <>
            Nicht ungeduldig werden — wer das Limit manuell hochsetzt, ruiniert sich die Reputation.
          </>,
          <>
            Pausierter Account? Erst Bounces unter "Bounce-Monitor" anschauen, dann Steigerung
            wieder starten.
          </>,
        ]}
        glossary={[
          {
            term: "Warmup",
            def: "Langsames Aufwärmen einer neuen Absende-Adresse — Tag für Tag etwas mehr Mails.",
          },
          {
            term: "Tageslimit",
            def: "Wieviele Mails dieser Absender heute maximal verschicken darf.",
          },
          {
            term: "Bounce-Rate",
            def: "Anteil der nicht zustellbaren Mails. Über 5% = Warnsignal, über 10% = sofort stoppen.",
          },
        ]}
      />

      {isLoading ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">Lade …</Card>
      ) : !data?.accounts.length ? (
        <Card className="p-12 text-center bg-card/40 border-dashed">
          <Flame className="size-10 mx-auto text-muted-foreground mb-3" />
          <p className="font-medium">Noch keine SMTP-Konten</p>
          <p className="text-sm text-muted-foreground">
            Lege zuerst ein SMTP-Konto unter "SMTP-Versand" an.
          </p>
        </Card>
      ) : (
        <div className="space-y-6">
          <OverviewSection summary={data.summary} />
          <div className="space-y-4">
            {data.accounts.map((a) => (
              <WarmupCard key={a.id} acc={a} onUpdate={mUpdate.mutate} />
            ))}
          </div>
          <EventsSection events={data.recent_events} accounts={data.accounts} />
          <DispatchesSection dispatches={data.recent_dispatches ?? []} accounts={data.accounts} />
        </div>
      )}
    </div>
  );
}

type Summary = {
  total: number;
  active: number;
  paused: number;
  disabled: number;
  auto_paused_last_7d: number;
  last_tick_at: string | null;
  alerts_last_7d?: number;
  alerts_failed_last_7d?: number;
};

function OverviewSection({ summary }: { summary: Summary }) {
  const fmt = (d: string | null) =>
    d ? new Date(d).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" }) : "—";
  const stat = (
    icon: React.ReactNode,
    label: string,
    value: string | number,
    tone: "default" | "good" | "warn" | "bad" = "default",
  ) => {
    const color =
      tone === "good"
        ? "text-green-600"
        : tone === "warn"
          ? "text-amber-600"
          : tone === "bad"
            ? "text-destructive"
            : "text-foreground";
    return (
      <Card className="p-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {icon}
          <span>{label}</span>
        </div>
        <div className={`text-2xl font-semibold mt-1 ${color}`}>{value}</div>
      </Card>
    );
  };
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      {stat(<Activity className="size-4" />, "Aktiv", summary.active, "good")}
      {stat(
        <PauseCircle className="size-4" />,
        "Pausiert",
        summary.paused,
        summary.paused > 0 ? "bad" : "default",
      )}
      {stat(<PowerOff className="size-4" />, "Inaktiv", summary.disabled)}
      {stat(
        <AlertTriangle className="size-4" />,
        "Auto-Pausen (7T)",
        summary.auto_paused_last_7d,
        summary.auto_paused_last_7d > 0 ? "warn" : "good",
      )}
      {stat(<CheckCircle2 className="size-4" />, "Letzte Prüfung", fmt(summary.last_tick_at))}
      {stat(
        <Bell className="size-4" />,
        "Warnungen (7T)",
        summary.alerts_last_7d ?? 0,
        (summary.alerts_failed_last_7d ?? 0) > 0 ? "warn" : "default",
      )}
      {stat(
        <BellOff className="size-4" />,
        "Fehlgeschlagen (7T)",
        summary.alerts_failed_last_7d ?? 0,
        (summary.alerts_failed_last_7d ?? 0) > 0 ? "bad" : "good",
      )}
    </div>
  );
}

type EventRow = {
  id: string;
  account_id: string;
  action: string;
  reason: string | null;
  sent_24h: number;
  bounced_24h: number;
  complaints_24h: number;
  bounce_rate: number;
  complaint_rate: number;
  previous_limit: number | null;
  new_limit: number | null;
  created_at: string;
};

function EventsSection({ events, accounts }: { events: EventRow[]; accounts: Acc[] }) {
  const labelOf = (id: string) => accounts.find((a) => a.id === id)?.label ?? id.slice(0, 8);
  const actionBadge = (a: string) => {
    if (a === "paused") return <Badge variant="destructive">Auto-Pause</Badge>;
    if (a === "grown") return <Badge>Limit erhöht</Badge>;
    if (a === "max_reached") return <Badge variant="secondary">Max erreicht</Badge>;
    return <Badge variant="outline">{a}</Badge>;
  };
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-medium">Verlauf der Warmup-Prüfungen</div>
          <div className="text-xs text-muted-foreground">
            Letzte automatische und manuelle Ticks. Bei Auto-Pause wird zusätzlich eine
            Telegram-Benachrichtigung gesendet.
          </div>
        </div>
      </div>
      {events.length === 0 ? (
        <div className="text-sm text-muted-foreground py-4">Noch keine Ereignisse.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="py-2 pr-3">Zeit</th>
                <th className="py-2 pr-3">Konto</th>
                <th className="py-2 pr-3">Aktion</th>
                <th className="py-2 pr-3">Gesendet 24h</th>
                <th className="py-2 pr-3">Bounce</th>
                <th className="py-2 pr-3">Complaint</th>
                <th className="py-2 pr-3">Limit</th>
                <th className="py-2 pr-3">Grund</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.id} className="border-t border-border/40">
                  <td className="py-2 pr-3 whitespace-nowrap">
                    {new Date(e.created_at).toLocaleString("de-DE", {
                      dateStyle: "short",
                      timeStyle: "short",
                    })}
                  </td>
                  <td className="py-2 pr-3">{labelOf(e.account_id)}</td>
                  <td className="py-2 pr-3">{actionBadge(e.action)}</td>
                  <td className="py-2 pr-3">{e.sent_24h}</td>
                  <td className="py-2 pr-3">
                    {e.bounced_24h} ({(Number(e.bounce_rate) * 100).toFixed(1)}%)
                  </td>
                  <td className="py-2 pr-3">
                    {e.complaints_24h} ({(Number(e.complaint_rate) * 100).toFixed(2)}%)
                  </td>
                  <td className="py-2 pr-3">
                    {e.previous_limit ?? "—"} → {e.new_limit ?? "—"}
                  </td>
                  <td className="py-2 pr-3 text-muted-foreground">{e.reason ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

type Acc = {
  id: string;
  label: string;
  from_email: string;
  warmup_enabled: boolean;
  warmup_start_limit: number;
  warmup_current_limit: number;
  warmup_max_limit: number;
  warmup_growth_percent: number;
  warmup_paused: boolean;
  warmup_pause_reason: string | null;
  sent_today: number;
  bounced_today: number;
  last_check: {
    action: string;
    reason: string | null;
    sent_24h: number;
    bounced_24h: number;
    complaints_24h: number;
    bounce_rate: number;
    complaint_rate: number;
    created_at: string;
  } | null;
};

function WarmupCard({
  acc,
  onUpdate,
}: {
  acc: Acc;
  onUpdate: (v: {
    id: string;
    warmup_enabled?: boolean;
    warmup_start_limit?: number;
    warmup_current_limit?: number;
    warmup_max_limit?: number;
    warmup_growth_percent?: number;
    warmup_paused?: boolean;
    warmup_pause_reason?: string | null;
  }) => void;
}) {
  const [startLimit, setStartLimit] = useState(acc.warmup_start_limit);
  const [maxLimit, setMaxLimit] = useState(acc.warmup_max_limit);
  const [growth, setGrowth] = useState(acc.warmup_growth_percent);

  const usedPct = Math.min(100, (acc.sent_today / Math.max(1, acc.warmup_current_limit)) * 100);

  return (
    <Card className="p-5 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-medium">
            {acc.label} <span className="text-xs text-muted-foreground">({acc.from_email})</span>{" "}
            {acc.warmup_paused ? (
              <Badge variant="destructive" className="ml-1">
                Pausiert
              </Badge>
            ) : acc.warmup_enabled ? (
              <Badge className="ml-1">Aktiv</Badge>
            ) : (
              <Badge variant="outline" className="ml-1">
                Inaktiv
              </Badge>
            )}
          </div>
          {acc.warmup_paused && (
            <div className="text-xs text-destructive mt-1">
              Pausiert: {acc.warmup_pause_reason ?? "Manuell pausiert"}
            </div>
          )}
          {acc.last_check && (
            <div className="text-xs text-muted-foreground mt-1">
              Letzte Prüfung{" "}
              {new Date(acc.last_check.created_at).toLocaleString("de-DE", {
                dateStyle: "short",
                timeStyle: "short",
              })}
              : 24h-Versand {acc.last_check.sent_24h}, Bounce{" "}
              {(Number(acc.last_check.bounce_rate) * 100).toFixed(1)}%, Complaint{" "}
              {(Number(acc.last_check.complaint_rate) * 100).toFixed(2)}%
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Warmup aktiv</span>
          <Switch
            checked={acc.warmup_enabled}
            onCheckedChange={(v) => onUpdate({ id: acc.id, warmup_enabled: v })}
          />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
          <span>
            Heute: <strong>{acc.sent_today}</strong> / {acc.warmup_current_limit} gesendet
          </span>
          <span>Bounces heute: {acc.bounced_today}</span>
        </div>
        <Progress value={usedPct} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs">Start-Limit</Label>
          <Input
            type="number"
            min={1}
            value={startLimit}
            onChange={(e) => setStartLimit(Number(e.target.value))}
          />
        </div>
        <div>
          <Label className="text-xs">Max-Limit</Label>
          <Input
            type="number"
            min={1}
            value={maxLimit}
            onChange={(e) => setMaxLimit(Number(e.target.value))}
          />
        </div>
        <div>
          <Label className="text-xs">Wachstum %/Tag</Label>
          <Input
            type="number"
            min={1}
            max={100}
            value={growth}
            onChange={(e) => setGrowth(Number(e.target.value))}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="secondary"
          onClick={() =>
            onUpdate({
              id: acc.id,
              warmup_start_limit: startLimit,
              warmup_max_limit: maxLimit,
              warmup_growth_percent: growth,
            })
          }
        >
          Werte speichern
        </Button>
        {acc.warmup_paused ? (
          <Button
            size="sm"
            onClick={() =>
              onUpdate({ id: acc.id, warmup_paused: false, warmup_pause_reason: null })
            }
          >
            <PlayCircle className="size-4 mr-1" /> Fortsetzen
          </Button>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              onUpdate({
                id: acc.id,
                warmup_paused: true,
                warmup_pause_reason: "Manuell pausiert",
              })
            }
          >
            <PauseCircle className="size-4 mr-1" /> Pausieren
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={() => onUpdate({ id: acc.id, warmup_current_limit: startLimit })}
        >
          Auf Start zurücksetzen
        </Button>
      </div>
    </Card>
  );
}
type DispatchRow = {
  id: string;
  account_id: string;
  channel: string;
  target: string | null;
  status: string;
  error_message: string | null;
  reason: string | null;
  sent_24h: number;
  bounced_24h: number;
  complaints_24h: number;
  bounce_rate: number;
  complaint_rate: number;
  current_limit: number | null;
  created_at: string;
};

function DispatchesSection({
  dispatches,
  accounts,
}: {
  dispatches: DispatchRow[];
  accounts: Acc[];
}) {
  const labelOf = (id: string) => accounts.find((a) => a.id === id)?.label ?? id.slice(0, 8);
  const channelBadge = (c: string) => {
    if (c === "email")
      return (
        <Badge variant="outline" className="gap-1">
          <Mail className="size-3" /> E-Mail
        </Badge>
      );
    if (c === "slack")
      return (
        <Badge variant="outline" className="gap-1">
          <MessageSquare className="size-3" /> Slack
        </Badge>
      );
    if (c === "telegram")
      return (
        <Badge variant="outline" className="gap-1">
          <Send className="size-3" /> Telegram
        </Badge>
      );
    return <Badge variant="outline">{c}</Badge>;
  };
  const statusBadge = (s: string) => {
    if (s === "sent")
      return (
        <Badge className="gap-1">
          <CheckCircle2 className="size-3" /> Gesendet
        </Badge>
      );
    if (s === "failed")
      return (
        <Badge variant="destructive" className="gap-1">
          <XCircle className="size-3" /> Fehler
        </Badge>
      );
    return <Badge variant="secondary">{s}</Badge>;
  };
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-medium">Versendete Auto-Pause-Warnungen</div>
          <div className="text-xs text-muted-foreground">
            Historie aller Telegram-, Slack- und E-Mail-Benachrichtigungen, die bei Auto-Pause
            ausgelöst wurden, inklusive Sende-Status.
          </div>
        </div>
      </div>
      {dispatches.length === 0 ? (
        <div className="text-sm text-muted-foreground py-4">Noch keine Warnungen versendet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="py-2 pr-3">Zeit</th>
                <th className="py-2 pr-3">Konto</th>
                <th className="py-2 pr-3">Kanal</th>
                <th className="py-2 pr-3">Ziel</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Ursache</th>
                <th className="py-2 pr-3">24h (Sent/Bnc/Cmp)</th>
                <th className="py-2 pr-3">Limit</th>
              </tr>
            </thead>
            <tbody>
              {dispatches.map((d) => (
                <tr key={d.id} className="border-t border-border/40 align-top">
                  <td className="py-2 pr-3 whitespace-nowrap">
                    {new Date(d.created_at).toLocaleString("de-DE", {
                      dateStyle: "short",
                      timeStyle: "short",
                    })}
                  </td>
                  <td className="py-2 pr-3">{labelOf(d.account_id)}</td>
                  <td className="py-2 pr-3">{channelBadge(d.channel)}</td>
                  <td
                    className="py-2 pr-3 max-w-[200px] truncate text-muted-foreground"
                    title={d.target ?? ""}
                  >
                    {d.target ?? "—"}
                  </td>
                  <td className="py-2 pr-3">
                    {statusBadge(d.status)}
                    {d.error_message && (
                      <div
                        className="text-[10px] text-destructive mt-1 max-w-[240px] truncate"
                        title={d.error_message}
                      >
                        {d.error_message}
                      </div>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-muted-foreground max-w-[220px]">
                    {d.reason ?? ""}
                  </td>
                  <td className="py-2 pr-3 whitespace-nowrap">
                    {d.sent_24h} / {d.bounced_24h} ({(Number(d.bounce_rate) * 100).toFixed(1)}%) /{" "}
                    {d.complaints_24h} ({(Number(d.complaint_rate) * 100).toFixed(2)}%)
                  </td>
                  <td className="py-2 pr-3">{d.current_limit ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
